import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';
import Spinner from '../../components/Spinner';

const MyOrders = () => {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryLoading, setRetryLoading] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const paymentSuccess = new URLSearchParams(location.search).get('payment') === 'success';

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setOrders(data);
        }
      } catch (err) {
        console.error("Failed to load orders", err);
      }
      setLoading(false);
    };

    if (token) {
      fetchOrders();
    }
  }, [token]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[24px] font-bold text-white mb-1">My Order History</h1>
        <p className="text-[14px] text-on-surface-variant">
          Track the status, payment records, and blockchain transaction details of your purchases.
        </p>
      </div>

      {paymentSuccess && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 p-4 rounded-lg flex items-center gap-3 animate-fade-in">
          <span className="material-symbols-outlined">check_circle</span>
          <span>Payment session successful! Your order is now pending blockchain confirmation.</span>
        </div>
      )}

      <div className="bg-surface-container border border-white/5 rounded-3xl overflow-hidden shadow-lg overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/5 text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
              <th className="px-6 py-4 whitespace-nowrap">ORDER ID</th>
              <th className="px-6 py-4 whitespace-nowrap">EA PRODUCT</th>
              <th className="px-6 py-4 whitespace-nowrap">AMOUNT</th>
              <th className="px-6 py-4 whitespace-nowrap">CRYPTO USED</th>
              <th className="px-6 py-4 whitespace-nowrap">TRANSACTION ID</th>
              <th className="px-6 py-4 whitespace-nowrap">STATUS</th>
              <th className="px-6 py-4 whitespace-nowrap">DELIVERY</th>
              <th className="px-6 py-4 whitespace-nowrap">DATE</th>
            </tr>
          </thead>
          <tbody className="text-[13px] text-white">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-12">
                  <Spinner text="Loading orders..." />
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order) => (
                <tr key={order.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 font-bold whitespace-nowrap">{order.id}</td>
                  <td className="px-6 py-4 font-bold whitespace-nowrap">{order.product_name}</td>
                  <td className="px-6 py-4 text-[#20b2aa] font-bold whitespace-nowrap">{order.amount}</td>
                  <td className="px-6 py-4 font-bold whitespace-nowrap">{order.crypto_used}</td>
                  <td className="px-6 py-4 text-on-surface-variant font-mono whitespace-nowrap">{order.tx_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${
                        order.status === 'EXPIRED' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 
                        order.status === 'COMPLETED' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 
                        'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}>
                        {order.status}
                      </span>
                      {order.status === 'EXPIRED' && (
                        <button 
                          onClick={async () => {
                            try {
                              setRetryLoading(order.id);
                              const res = await fetch('http://localhost:5000/api/retry-invoice', {
                                method: 'POST',
                                headers: { 
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ orderId: order.id })
                              });
                              const data = await res.json();
                              if (data.invoice_url && data.orderId) {
                                navigate(`/payment/status/${data.orderId}`, { state: { invoice_url: data.invoice_url } });
                              } else {
                                alert('Failed to generate retry invoice.');
                                setRetryLoading(null);
                              }
                            } catch (e) {
                              alert('Failed to generate retry invoice.');
                              setRetryLoading(null);
                            }
                          }}
                          disabled={retryLoading === order.id}
                          className="text-primary hover:underline text-[12px] font-bold flex items-center gap-1 disabled:opacity-50"
                        >
                          {retryLoading === order.id ? 'Loading...' : (
                            <>Retry <span className="material-symbols-outlined text-[14px]">arrow_right_alt</span></>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-on-surface-variant whitespace-nowrap">{order.delivery}</td>
                  <td className="px-6 py-4 text-on-surface-variant whitespace-nowrap">{new Date(order.created_at).toLocaleDateString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-on-surface-variant">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyOrders;
